import { getServerSession } from '@/@auth';
import { tracking } from '@/lib/features/tracking';
import { EVENTS } from '@/lib/features/tracking/events';
import { getSession } from '@/providers/enhanced-session';

export interface ServiceConfig {
    serviceName: string;
    featureName: string;
}

/**
 * Gets the session token, handling both client and server contexts
 */
export async function getSessionToken(config: ServiceConfig): Promise<string | null> {
    const { serviceName, featureName } = config;
    try {
        // Try client-side first
        const clientSession = await getSession();
        tracking.event(EVENTS.USER.SESSION_CHECK, {
            hasToken: !!clientSession?.idToken,
            hasUser: !!clientSession,
            context: 'client',
            feature: featureName,
        });

        if (clientSession?.idToken) {
            return clientSession.idToken;
        }

        // If client-side fails, try server-side
        const serverSession = await getServerSession();
        tracking.event(EVENTS.USER.SESSION_CHECK, {
            hasToken: !!serverSession?.idToken,
            hasUser: !!serverSession,
            context: 'server',
            feature: featureName,
        });

        if (serverSession?.idToken) {
            return serverSession.idToken;
        }

        tracking.event(EVENTS.USER.SESSION_ERROR, {
            error: 'No session token found in either client or server context',
            feature: featureName,
            hasToken: false,
        });
        return null;
    } catch (error) {
        tracking.errorWithSource(
            error instanceof Error ? error : new Error(String(error)),
            `${serviceName}.getSessionToken`,
            {
                operation: 'session_retrieval',
                phase: 'token_fetch',
                error: error instanceof Error ? error.message : String(error),
                feature: featureName,
            },
        );
        return null;
    }
}
